<template>
    <app-layout title="Dashboard">
        <div class="lg:flex">
            <sidebar-catalogue :categories="categories"/>

            <div class="flex-1 catalogue">
                <div v-if="isSchoolCustomer" class="xl:hidden px-2 pt-1 sm:pt-2 pb-0 lg:px-8 sm:font-semibold text-gray-500 text-xs text-center">
                    <div class="sm:hidden">Order deadline is Wed. @ 11:59pm for delivery the next week</div>
                    <div class="hidden sm:block sm:text-sm">Order deadline is Wednesday at 11:59pm for delivery the following week</div>
                </div>

                <div class="border-b border-gray bg-white sticky top-0 z-10 p-2 lg:px-8 md:flex justify-between items-center">
                    <form @submit.prevent="handleSearch" class="md:flex gap-x-4 w-full" style="max-width: 800px">
                        <div class="button-group flex-1">
                            <select v-model="searchForm.category" class="">
                                <option value="">All Categories</option>
                                <option :value="category.slug" v-for="category in categories" :key="category.id">
                                    <span v-if="category.parent_id">---</span>
                                    {{ category.title }}
                                </option>
                            </select>

                            <input
                                v-model="searchForm.search"
                                type="text"
                                class="w-full"
                                placeholder="Search..."
                                ref="searchForm"
                            />

                            <button type="submit" class="rounded-r border-l-0 p-0">
                                <img src="/images/icons/search.svg" width="18" height="18" alt="Search"  class="mx-auto"/>
                            </button>
                        </div>

                        <div class="flex justify-between gap-x-4 items-center mt-2 md:my-0">
                            <div class="flex items-center gap-x-2">
                                <toggle v-model="searchForm.local" />
                                <jet-label for="local" value="Local Only" class="mb-0" />
                            </div>

                            <div class="xl:hidden">
                                <cart-widget-mobile class="" />
                            </div>
                        </div>
                    </form>

                    <div v-if="isSchoolCustomer" class="hidden xl:block font-semibold text-gray-500 text-xs text-center" style="max-width:275px">
                        Order Deadline is Wednesday at 11:59pm for delivery the following week
                    </div>

                    <div class="hidden xl:block">
                        <cart-widget v-if="isCustomer" class=""/>
                    </div>
                </div>

                <div class="py-4 px-3 lg:px-5">
                    <div v-for="categoryData in productsByCategory" :key="categoryData.category.id">
                        <h2 class="category-title">
                            {{ categoryData.category.title }}
                            <span v-if="isSubcategory"> - {{ currentCategory.title }}</span>
                        </h2>
                        <div class="flex flex-wrap -mx-3">
                            <div v-for="product in categoryData.products" :key="product.id" class="w-full xs:w-1/2 md:w-1/4 xl:w-1/6 px-3 mb-6">
                                <product-summary :product="product" />
                            </div>

                            <p v-if="categoryData.products.length === 0" class="text-center text-xl w-full">
                                No products at this time.
                            </p>
                        </div>
                    </div>

                    <div v-if="productsByCategory.length === 0">
                        <h2 v-if="currentCategory" class="category-title">
                            <div v-if="isSubcategory">
                                {{ currentParentCategory.title }} - {{ currentCategory.title }}
                            </div>
                            <div v-else>
                                {{ currentCategory.title }}
                            </div>
                        </h2>

                        <p class="text-center text-xl w-full">
                            No products at this time.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import { onMounted } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue'
import SidebarCatalogue from '@/Layouts/SidebarCatalogue.vue';
import CartWidget from '@/Pages/Catalogue/Partials/CartWidget.vue';
import CartWidgetMobile from '@/Pages/Catalogue/Partials/CartWidgetMobile.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetLabel from '@/Jetstream/Label';
import ProductSummary from '@/Pages/Catalogue/Partials/ProductSummary';
import JetButton from '@/Jetstream/Button';
import UseCart from '@/Composables/UseCart';
import Toggle from '@vueform/toggle';

import '@vueform/toggle/themes/default.css';

export default {
    setup () {
        const { isCustomer } = UseUserAuth(usePage().props.value.user);
        const { fetchCart } = 
            UseCart(
                usePage().props.value.user,
                usePage().props.value.activeCustomer ? usePage().props.value.activeCustomer.tax_exempt : 0
            );

        if (isCustomer.value) {
            onMounted(fetchCart);
        }

        return {
            isCustomer,
            fetchCart,
        }
    },

    components: {
        AppLayout,
        SidebarCatalogue,
        CartWidget,
        CartWidgetMobile,
        JetInput,
        JetLabel,
        ProductSummary,
        JetButton,
        Toggle,
    },

    props: ['categories', 'slug', 'products', 'user'],

    data() {
        return {
            searchForm: this.$inertia.form({
                search: this.route().params.search,
                category: this.route().params.category ?? "",
                local: this.route().params.local === 'true' ? true : '',
            }),
            
            showStaticCategories: this.slug === null || this.route().current() === 'catalogue.hot_buys', 
            showDynamicCategories: this.route().current() !== 'catalogue.hot_buys',

            staticCategories: [
                { id: null, parent_id: null, order_index: null, slug: 'hot_buys', title: 'Hot Buys',  subcategories: []},
            ]
        }
    },

    methods: {
        handleSearch () {
            this.searchForm.get(this.route('catalogue', this.searchForm.category !== null ? this.searchForm.category : ''))
        },

        handleFilterLocal () {
            this.searchForm.get(this.route('catalogue', this.slug !== null ? this.slug : ''))
        },
    },

    computed: {
        activeCustomer() {
            return this.$page.props.activeCustomer;
        },

        isSchoolCustomer() {
            return this.activeCustomer?.type === 'School';
        },

        currentCategory() {
            return this.allCategories.find(c => c.slug === this.slug);
        },

        currentParentCategory() {
            let currentParentCategory = null;

            if (this.isSubcategory)
            {
                currentParentCategory = this.allCategories.find(c => c.id === this.currentCategory.parent_id);
            }

            return currentParentCategory;
        },

        isSubcategory() {
            let isSubcategory = false;

            if (this.currentCategory !== undefined && this.currentCategory.parent_id !== null)
            {
                isSubcategory = true;
            }

            return isSubcategory;
        },

        allCategories() {
            let staticCategories = (this.showStaticCategories ? this.staticCategories : []);
            let dynamicCategories = (this.showDynamicCategories ? this.categories : []);

            return staticCategories.concat(dynamicCategories);
        },

        availableCategories () {
            let availableCategories = [];

            this.allCategories.forEach(c => {
                const match = (Object.values(this.products)).find(p => {
                    if (c.slug === 'hot_buys') {
                        return p.hot_buy === true;
                    }

                    if (c.parent_id !== null) return false;

                    const submatch = c.subcategories.find(sub => p.category_id === sub.id);

                    return (submatch || p.category_id === c.id) && p.enabled === true;
                });

                if (match) {
                    availableCategories.push(c);
                }
            });

            return availableCategories;
        },

        productsByCategory() {
            let productsByCategory = [];

            this.availableCategories.forEach(c => {
                let categoryProducts = (Object.values(this.products)).filter(p => {
                    if (c.slug === 'hot_buys') {
                        return p.hot_buy === true;
                    }
                    
                    const submatch = c.subcategories.find(sub => p.category_id === sub.id);

                    return (submatch || p.category_id === c.id) && p.enabled === true;
                });

                productsByCategory.push({
                    category: c,
                    products: categoryProducts
                });
            });

            return productsByCategory;
        }
    },

    watch: {
        'searchForm.local': {
            deep: true,
            handler: function() {
                this.handleFilterLocal();
            }
        },
        activeCustomer: {
            handler(newVal, oldVal) {
                this.fetchCart();
            },
            deep: true
        },
    },

    mounted () {
        if (this.route().params.search) {
            this.$refs.searchForm.focus();
        }
    }
}
</script>

<style scoped>
.button-group button {
    min-width: 34px;
}

@media (min-width: 1024px) {
    .catalogue {
        padding-left: 240px;
    }
}

.category-title {
    text-align: center;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #85C71E;
}

@media (min-width: 768px) {
    .category-title {
        font-size: 2.375rem;
        text-align: left;
    }
}
</style>